import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { selectedDiagnosticReportsFilter } from '../../redux/reducers/filterDiagnosticReport';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { DiagnosticsGetStudentsTableQuery } from '../../graphQLOperations/__generated__/DiagnosticsGetStudentsTableQuery.graphql';
import { DiagnosticReportStudentsTable } from './DiagnosticReportStudentsTable';
import { ProjectionGroup } from '../../models/ProjectionGroupEnum';
import { useAppSelector } from '../../redux/hooks';
import { getStudentsDiganosticReportTableInfo } from '../../graphQLOperations/Diagnostics';
import { Card } from 'primereact/card';
import BaselineProjectionModal from './BaselineProjectionModal';
import { Divider } from 'primereact/divider';
import { DiagnosticsStatusByClassReportQuery } from '../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';

interface Props {
  studentsTableQueryRef: PreloadedQuery<DiagnosticsGetStudentsTableQuery, Record<string, unknown>> | null | undefined;
  inProgressQueryRef: PreloadedQuery<DiagnosticsStatusByClassReportQuery, Record<string, unknown>> | null | undefined;
  setRefetchKey: Dispatch<SetStateAction<number>>;
}

export interface ProjectionSettings {
  threeSkillsPerWeek: boolean;
  selectedStatus: ProjectionGroup;
}
export const initialProjectionSettings = { threeSkillsPerWeek: false, selectedStatus: ProjectionGroup.NOTSET };

export const StudentsCompletedGateWrapper: FC<Props> = ({
  studentsTableQueryRef,
  inProgressQueryRef,
  setRefetchKey,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [projectionSettings, setProjectionSettings] = useState<ProjectionSettings>(initialProjectionSettings);
  const reportFilters = useAppSelector(selectedDiagnosticReportsFilter);
  useEffect(() => {
    setProjectionSettings(initialProjectionSettings);
  }, [reportFilters]);

  return (
    <>
      <Card
        pt={{
          content: {
            className: '!tw-pt-0',
          },
        }}
        className="tw-w-full !tw-shadow-none tw-border tw-border-solid tw-border-neutral-300"
      >
        <div className="tw-flex tw-flex-col tw-w-full ">
          <h1 className="tw-font-sora tw-text-xl tw-text-neutral-950">Baseline Student Results</h1>
          <p className="tw-m-0 tw-font-atkinson tw-text-base tw-text-neutral-700">
            Each student’s score reflects the skills they demonstrated on the date they completed the diagnostic. This
            is their starting point, which feeds into their{' '}
            <span
              className="tw-text-indigo-600 tw-font-bold tw-underline tw-cursor-pointer"
              onClick={() => setVisible(true)}
            >
              Baseline Projection.
            </span>
          </p>
        </div>
        <Divider className="tw-w-full tw-m-0" />
        {studentsTableQueryRef && inProgressQueryRef && (
          <StudentsCompletedGate
            studentsTableQueryRef={studentsTableQueryRef}
            inProgressQueryRef={inProgressQueryRef}
            projectionSettings={projectionSettings}
            setRefetchKey={setRefetchKey}
            classIds={reportFilters?.classes?.length ? reportFilters.classes.map(c => c.id) : undefined}
          />
        )}
      </Card>
      {visible && <BaselineProjectionModal visible={visible} setVisible={setVisible} />}
    </>
  );
};
const StudentsCompletedGate: FC<{
  studentsTableQueryRef: PreloadedQuery<DiagnosticsGetStudentsTableQuery, Record<string, unknown>>;
  inProgressQueryRef: PreloadedQuery<DiagnosticsStatusByClassReportQuery, Record<string, unknown>>;
  projectionSettings: ProjectionSettings;
  setRefetchKey: Dispatch<SetStateAction<number>>;
  classIds?: string[];
}> = ({ studentsTableQueryRef, inProgressQueryRef, projectionSettings, setRefetchKey, classIds }) => {
  const data = usePreloadedQuery<DiagnosticsGetStudentsTableQuery>(
    getStudentsDiganosticReportTableInfo,
    studentsTableQueryRef,
  );
  const report = data.classDiagnosticReport;
  const hasAnyCompleted = Array.isArray(report) && report.some(item => !!item?.diagnosticCompleted);
  if (!hasAnyCompleted) {
    return (
      <div className="mt-6 w-full text-center p-4">
        <span className="tw-px-20 tw-py-5 tw-rounded-lg tw-bg-indigo-100 tw-font-atkinson tw-font-bold">
          No diagnostic results yet
        </span>
      </div>
    );
  }
  return (
    <>
      <DiagnosticReportStudentsTable
        setRefetchKey={setRefetchKey}
        studentsTableQueryRef={studentsTableQueryRef}
        inProgressQueryRef={inProgressQueryRef}
        projectionSettings={projectionSettings}
        classIds={classIds}
      />
    </>
  );
};
